/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { JSX } from 'react';
import type { EditorKey } from './editorSettings';


import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { INITIAL_SETTINGS } from './editorSettings';

type EditorSettingsContextShape = {
  setOption: (name: EditorKey, value: any) => void;
  settings: Record<EditorKey, any>;
};

const Context: React.Context<EditorSettingsContextShape> = createContext({
  setOption: (_name: EditorKey, _value: any) => {
    return;
  },
  settings: INITIAL_SETTINGS,
});

export const EditorSettingsContext = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [settings, setSettings] = useState(INITIAL_SETTINGS);

  const setOption = useCallback((setting: EditorKey, value: any) => {
    setSettings((options: Record<EditorKey, any>) => ({
      ...options,
      [setting]: value,
    }));
  }, []);

  const contextValue = useMemo(() => {
    return { setOption, settings };
  }, [setOption, settings]);

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export const useEditorSettings = (): EditorSettingsContextShape => {
  return useContext(Context);
};